import { readFirstTouch, type FirstTouchAttribution } from "./first-touch";
import { isAnalyticsPermitted, isPosthogLoaded, posthog, applyCapture } from "./posthog";
import { isAdminRoute } from "./sensitive-routes";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * URL of the last `$pageview` this tab actually sent. The pre-consent capture
 * is not recorded here, so accepting cookies can still send the landing page
 * once. A later navigation has a different URL and sends its own pageview.
 */
let lastCapturedUrl: string | null = null;

export function resetPageViewCaptureForTests(): void {
  lastCapturedUrl = null;
}

export function buildPageViewProperties(input: {
  origin: string;
  pathname: string;
  search: string;
  referrer: string;
  recoverFirstTouch: boolean;
  firstTouch: FirstTouchAttribution;
}): Record<string, string> {
  const search = input.search
    ? input.search.startsWith("?")
      ? input.search
      : `?${input.search}`
    : "";
  const properties: Record<string, string> = {
    $current_url: `${input.origin}${input.pathname}${search}`,
  };

  if (input.referrer) {
    properties.$referrer = input.referrer;
    try {
      const host = new URL(input.referrer).hostname;
      if (host) {
        properties.referrer_host = host;
      }
    } catch {
      // A malformed referrer still rides on $referrer as the browser sent it.
    }
  }

  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      properties[key] = value;
    }
  }

  if (!input.recoverFirstTouch) {
    return properties;
  }

  const touch = input.firstTouch;
  if (!properties.utm_source && touch.utmSource) properties.utm_source = touch.utmSource;
  if (!properties.utm_medium && touch.utmMedium) properties.utm_medium = touch.utmMedium;
  if (!properties.utm_campaign && touch.utmCampaign) properties.utm_campaign = touch.utmCampaign;
  if (!properties.utm_content && touch.utmContent) properties.utm_content = touch.utmContent;
  if (!properties.utm_term && touch.utmTerm) properties.utm_term = touch.utmTerm;
  if (!properties.referrer_host && touch.referrerHost) {
    properties.referrer_host = touch.referrerHost;
  }
  if (!properties.$referrer && touch.referrerHost) {
    properties.$referrer = `https://${touch.referrerHost}/`;
  }

  return properties;
}

/**
 * Send the current page once, after analytics is permitted.
 *
 * Call this on navigation and again when cookie consent flips to accepted.
 * The pre-consent attempt is dropped by the SDK and is not remembered, so the
 * accept path is the landing `$pageview`. The same URL is not sent twice.
 * `/admin/*` is skipped.
 */
export function captureCurrentPageView(options?: {
  recoverFirstTouch?: boolean;
}): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  applyCapture();

  if (!isAnalyticsPermitted() || !isPosthogLoaded()) {
    return false;
  }
  if (isAdminRoute(window.location.pathname)) {
    return false;
  }

  const recover = options?.recoverFirstTouch === true;
  const firstSend = lastCapturedUrl === null;
  const properties = buildPageViewProperties({
    origin: window.location.origin,
    pathname: window.location.pathname,
    search: window.location.search,
    // Later in-app navigations are not new landings. Only the first send
    // (and the consent recovery of the dropped one) carries the referrer.
    referrer: firstSend || recover ? document.referrer : "",
    recoverFirstTouch: recover,
    firstTouch: recover ? readFirstTouch() : {},
  });
  const url = properties.$current_url;
  if (!url || lastCapturedUrl === url) {
    return false;
  }
  lastCapturedUrl = url;
  posthog.capture("$pageview", properties);
  return true;
}
